// Controls + status for real-backend pricing, plus the row-4 verification.
//
// The pricing STATE lives in QuoteDetail, not here — pressing "Recalculate
// via backend" now drives the main MaterialTable's live columns, so the
// owner of that state has to be the page. This component renders the
// controls, the status line, and the live/est. legend.
//
// The row-4 check is the exception: it keeps its own local state because it
// posts a fixed known fixture and deliberately does NOT touch the quote's
// numbers. It is the sharpest single proof the real engine answered — row
// 4's raw Unit SP lands exactly on 10246.5, so 10247 only holds under the
// engine's ROUND_HALF_UP.

import { useState } from "react";
import { RefreshCw, CheckCircle2, AlertCircle } from "lucide-react";
import { api } from "@/services/api";
import type { BackendPricedLine } from "@/services/api";
import { ProvenanceBadge } from "@/components/MaterialTable";
import type { LiveStatus } from "@/components/MaterialTable";
import {
  colors, semanticColors, radius, spacing, typography, heights, iconSize, iconStroke,
} from "@/styles/tokens";

// The two confirmed engine percentages (engine/config.py's PricingConfig
// defaults, validated against real client data). The frontend has no
// pricing-config record of its own yet, so they are stated once here.
export const VARIANCE_PCT = 3.5;
export const GP_PCT = 28;

const ROW4 = {
  oem: "Netrack",
  part_number: "NRS-PD-48-800-C-Z0",
  qty: 23,
  unit_price: 7128.0,
  ibf_pct: 0,
};
const ROW4_EXPECTED_UNIT_SP = 10247;
const ROW4_EXPECTED_EXTENDED_SP = 235681;

const BTN_BASE = {
  height: heights.control,
  padding: `0 ${spacing[12]}px`,
  borderRadius: radius.base,
  fontSize: typography.fontSize.sm,
  fontWeight: typography.fontWeight.medium,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  gap: spacing[6],
} as const;

export function BackendPriceStrip({
  status,
  error,
  pricedCount,
  totalCount,
  onRecalculate,
}: {
  status: LiveStatus;
  error: string;
  pricedCount: number;
  totalCount: number;
  onRecalculate: () => void;
}) {
  const [row4Status, setRow4Status] = useState<LiveStatus>("idle");
  const [row4Error, setRow4Error] = useState("");
  const [row4, setRow4] = useState<BackendPricedLine | null>(null);

  async function verifyRow4() {
    setRow4Status("loading");
    setRow4Error("");
    const result = await api.priceLinesFromBackend([ROW4], VARIANCE_PCT, GP_PCT);
    if (!result.ok || !result.lines?.length) {
      setRow4(null);
      setRow4Error(result.error ?? "Backend reported an unknown failure.");
      setRow4Status("error");
      return;
    }
    setRow4(result.lines[0]);
    setRow4Status("ok");
  }

  const row4Matches =
    row4 !== null &&
    row4.unit_sp === ROW4_EXPECTED_UNIT_SP &&
    row4.extended_sp === ROW4_EXPECTED_EXTENDED_SP;

  const busy = status === "loading" || row4Status === "loading";

  return (
    <div
      data-testid="backend-price-strip"
      style={{
        marginTop: spacing[12],
        border: `1px solid ${semanticColors.border}`,
        borderRadius: radius.base,
        backgroundColor: semanticColors.panelBg,
      }}
    >
      <div
        className="flex items-center justify-between"
        style={{ padding: `${spacing[8]}px ${spacing[12]}px`, gap: spacing[12] }}
      >
        <div className="flex items-baseline" style={{ gap: spacing[8], flexWrap: "wrap" }}>
          <span style={{
            fontSize: typography.fontSize.sm,
            fontWeight: typography.fontWeight.medium,
            color: semanticColors.textPrimary,
          }}>
            Live engine pricing
          </span>
          <span style={{ fontSize: typography.fontSize.xs, color: semanticColors.textMuted }}>
            domestic path · variance {VARIANCE_PCT}% · GP {GP_PCT}%
          </span>
          {status === "ok" && (
            <span
              data-testid="backend-price-status-ok"
              className="tabular-nums"
              style={{ fontSize: typography.fontSize.xs, color: semanticColors.success }}
            >
              {pricedCount} of {totalCount} lines priced by the validated engine
            </span>
          )}
        </div>

        <div className="flex items-center" style={{ gap: spacing[8] }}>
          <button
            data-testid="backend-price-verify-row4"
            disabled={busy}
            onClick={verifyRow4}
            style={{
              ...BTN_BASE,
              backgroundColor: semanticColors.surface,
              color: semanticColors.textSecondary,
              border: `1px solid ${semanticColors.border}`,
              opacity: busy ? 0.6 : 1,
            }}
          >
            {row4Status === "loading" ? "Verifying…" : "Verify row 4"}
          </button>
          <button
            data-testid="backend-price-recalculate"
            disabled={busy || totalCount === 0}
            onClick={onRecalculate}
            style={{
              ...BTN_BASE,
              backgroundColor: colors.indigo600,
              color: semanticColors.onAccent,
              border: "none",
              opacity: busy || totalCount === 0 ? 0.6 : 1,
            }}
          >
            <RefreshCw
              size={iconSize.md}
              strokeWidth={iconStroke.regular}
              className={status === "loading" ? "animate-spin" : undefined}
            />
            {status === "loading" ? "Pricing…" : "Recalculate via backend"}
          </button>
        </div>
      </div>

      {/* Legend — explains the two header chips the table shows. */}
      <div
        data-testid="backend-price-legend"
        className="flex items-center"
        style={{
          padding: `${spacing[6]}px ${spacing[12]}px`,
          borderTop: `1px solid ${semanticColors.border}`,
          gap: spacing[6],
          fontSize: typography.fontSize.xs,
          color: semanticColors.textMuted,
          flexWrap: "wrap",
        }}
      >
        <ProvenanceBadge kind="live" />
        <span>= computed by the validated pricing engine.</span>
        <ProvenanceBadge kind="est" />
        <span>
          = placeholder math, pending the import-pricing engine (freight, duty and
          landed cost have no validated deal behind them yet).
        </span>
      </div>

      {status === "error" && (
        <div
          data-testid="backend-price-error"
          className="flex items-start"
          style={{
            padding: `${spacing[8]}px ${spacing[12]}px`,
            borderTop: `1px solid ${semanticColors.destructiveBorder}`,
            backgroundColor: semanticColors.destructiveBg,
            gap: spacing[8],
            fontSize: typography.fontSize.sm,
            color: semanticColors.destructive,
          }}
        >
          <AlertCircle size={iconSize.md} strokeWidth={iconStroke.regular} style={{ flexShrink: 0, marginTop: spacing[2] }} />
          <span>
            Pricing failed — the live columns are blank rather than showing
            placeholder math. {error}
          </span>
        </div>
      )}

      {row4Status === "error" && (
        <div
          data-testid="backend-price-row4-error"
          className="flex items-start"
          style={{
            padding: `${spacing[8]}px ${spacing[12]}px`,
            borderTop: `1px solid ${semanticColors.destructiveBorder}`,
            backgroundColor: semanticColors.destructiveBg,
            gap: spacing[8],
            fontSize: typography.fontSize.sm,
            color: semanticColors.destructive,
          }}
        >
          <AlertCircle size={iconSize.md} strokeWidth={iconStroke.regular} style={{ flexShrink: 0, marginTop: spacing[2] }} />
          <span>Row 4 check failed: {row4Error}</span>
        </div>
      )}

      {row4Status === "ok" && row4 !== null && (
        <div
          data-testid="backend-price-row4-result"
          className="flex items-center"
          style={{
            padding: `${spacing[8]}px ${spacing[12]}px`,
            borderTop: `1px solid ${row4Matches ? semanticColors.successBorder : semanticColors.destructiveBorder}`,
            backgroundColor: row4Matches ? semanticColors.successBg : semanticColors.destructiveBg,
            gap: spacing[8],
            fontSize: typography.fontSize.sm,
            color: row4Matches ? semanticColors.success : semanticColors.destructive,
          }}
        >
          {row4Matches
            ? <CheckCircle2 size={iconSize.md} strokeWidth={iconStroke.regular} style={{ flexShrink: 0 }} />
            : <AlertCircle size={iconSize.md} strokeWidth={iconStroke.regular} style={{ flexShrink: 0 }} />}
          <span className="tabular-nums">
            Row 4 (qty 23 × ₹7,128) → Unit SP{" "}
            <strong data-testid="backend-price-row4-unit-sp">{row4.unit_sp}</strong>
            , Extended SP <strong data-testid="backend-price-row4-extended-sp">{row4.extended_sp}</strong>
            {row4Matches
              ? " — matches the validated engine."
              : ` — EXPECTED ${ROW4_EXPECTED_UNIT_SP} / ${ROW4_EXPECTED_EXTENDED_SP}.`}
          </span>
        </div>
      )}
    </div>
  );
}
